import { createContext, useState, useEffect } from "react";

export const CartContext = createContext();

export function CartProvider({ children }) {
    const [cartItems, setCartItems] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [initialized, setInitialized] = useState(false);

    const openCart = () => setIsOpen(true);
    const closeCart = () => setIsOpen(false);
    const [cartSignal, setCartSignal] = useState(0);
    const [openInfoSignal, setOpenInfoSignal] = useState(null);

    // Load from order_express_items (single source of truth)
    useEffect(() => {
        const saved = localStorage.getItem("order_express_items");
        if (saved) setCartItems(JSON.parse(saved));
        setInitialized(true);
    }, []);

    // Persist to order_express_items only after initial load
    useEffect(() => {
        if (!initialized) return;
        localStorage.setItem("order_express_items", JSON.stringify(cartItems));
    }, [cartItems, initialized]);

    // Caller pre-computes merged qty/subtotal/tax — just replace or append
    const addToCart = (product) => {
        setCartItems((prev) => {
            const existing = prev.find((item) => item.idProduct === product.idProduct);
            if (existing) {
                return prev.map((item) =>
                    item.idProduct === product.idProduct
                        ? { ...item, ...product, notes: product.notes ?? item.notes ?? null }
                        : item
                );
            }
            return [...prev, product];
        });
        openCart();
    };

    const updateCartItem = (idProduct, newQty) => {
        setCartItems(prev =>
            prev.map(item =>
                item.idProduct === idProduct
                    ? { ...item, qty: newQty }
                    : item
            )
        );
    };

    const cartCount = cartItems.length;

    const removeFromCart = (idProduct) => {
        setCartItems(prev => prev.filter(item => item.idProduct !== idProduct));
        setCartSignal(prev => prev + 1);
    };

    const clearCart = () => {
        setCartItems([]);
        localStorage.removeItem("order_express_items");
    };

    // Silent version (no openCart) — same merge logic
    const addToCartSilent = (product) => {
        setCartItems((prev) => {
            const existing = prev.find((item) => item.idProduct === product.idProduct);
            if (existing) {
                return prev.map((item) =>
                    item.idProduct === product.idProduct
                        ? { ...item, ...product, notes: product.notes ?? item.notes ?? null }
                        : item
                );
            }
            return [...prev, product];
        });
    };

    return (
        <CartContext.Provider
            value={{
                cartItems,
                setCartItems,
                addToCart,
                addToCartSilent,
                removeFromCart,
                clearCart,
                updateCartItem,
                cartCount,
                isOpen,
                openCart,
                closeCart,
                cartSignal,
                setOpenInfoSignal
            }}
        >
            {children}
        </CartContext.Provider>
    );
}
